import { useRef, type ReactNode } from "react";
import { usePrefersReducedMotion } from "../hooks/usePrefersReducedMotion";

type TiltCardProps = {
  children: ReactNode;
  /** Maksymalny obrót w stopniach - 4-6 to smak, 10+ to jarmark */
  maxDeg?: number;
  className?: string;
};

/**
 * Subtelny 3D tilt karty za kursorem - perspective 800px, rotateX/rotateY
 * do maxDeg, sprężysty powrót po zjechaniu myszą. Czysty CSS transform.
 *
 * Kanoniczna kolejność zagnieżdżenia z resztą paczki (od zewnątrz):
 *   <Reveal>            - wejście przy scrollu
 *     <TiltCard>        - 3D za kursorem
 *       <SpotlightCard> - światło za kursorem
 *         ...karta...
 *
 * Tylko pointer: fine. Na dotyku i przy reduced-motion: przezroczysty wrapper.
 */
export function TiltCard({ children, maxDeg = 5, className }: TiltCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const reducedMotion = usePrefersReducedMotion();
  const finePointer =
    typeof window !== "undefined" && window.matchMedia && window.matchMedia("(pointer: fine)").matches;
  const enabled = !reducedMotion && finePointer;

  function onMouseMove(e: React.MouseEvent<HTMLDivElement>) {
    if (!enabled) return;
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    el.style.transform = `perspective(800px) rotateX(${(-py * maxDeg).toFixed(2)}deg) rotateY(${(px * maxDeg).toFixed(2)}deg)`;
    el.style.transition = "transform 120ms ease-out";
  }

  function onMouseLeave() {
    const el = ref.current;
    if (!el) return;
    el.style.transform = "perspective(800px) rotateX(0deg) rotateY(0deg)";
    el.style.transition = "transform 600ms cubic-bezier(0.16, 1, 0.3, 1)";
  }

  return (
    <div
      ref={ref}
      className={className}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      style={{ willChange: enabled ? "transform" : undefined }}
    >
      {children}
    </div>
  );
}
